import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { useCategories } from "@/context/CategoriesContext";
import { useAuth } from "@/context/AuthContext";
import BrandHeader from "@/components/BrandHeader";
import CategoryManager from "@/components/CategoryManager";
import UsersManager from "@/components/UsersManager";
import SettingsManager from "@/components/SettingsManager";
import AuditLog from "@/components/AuditLog";
import PromotionsManager from "@/components/PromotionsManager";
import ForgotPasswordForm from "@/components/ForgotPasswordForm";
import ResetPasswordForm from "@/components/ResetPasswordForm";
import {
  adminLogin, createItem, deleteItem, fetchMenu, fetchAdminMenu, updateItem,
  uploadImage, fetchAnalytics, getErrorMessage,
} from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Copy, Eye, Pencil, Plus, Trash2, Upload } from "lucide-react";
import VariantsEditor from "@/components/VariantsEditor";

const emptyForm = {
  name_en: "", name_ar: "", description_en: "", description_ar: "",
  category: "", price: "", calories: "",
  protein_g: "", carbs_g: "", fat_g: "",
  image_url: "", is_featured: false, kitchen_required: false,
  sizes: [], milk_options: [],
  is_active: true,
};

export default function AdminPage() {
  const { lang, t } = useLang();
  const { categories, nameFor } = useCategories();
  const { user, checking, refreshMe, logout, can, isSuperAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const resetTokenFromUrl = new URLSearchParams(location.search).get("reset_token");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginBusy, setLoginBusy] = useState(false);
  const [retryAt, setRetryAt] = useState(0); // epoch seconds when retry allowed
  const [now, setNow] = useState(Math.floor(Date.now() / 1000));
  const [authView, setAuthView] = useState("login"); // login | forgot | reset

  // tick down retry countdown
  useEffect(() => {
    if (retryAt <= now) return;
    const id = setInterval(() => setNow(Math.floor(Date.now() / 1000)), 1000);
    return () => clearInterval(id);
  }, [retryAt, now]);

  // If URL has ?reset_token=..., start in reset view
  useEffect(() => {
    if (resetTokenFromUrl && !user) setAuthView("reset");
  }, [resetTokenFromUrl, user]);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [uploading, setUploading] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [tab, setTab] = useState("items");
  const fileRef = useRef(null);

  const reload = async () => {
    setLoading(true);
    try { setItems(await fetchAdminMenu()); }
    finally { setLoading(false); }
  };

  const loadAnalytics = async () => {
    if (!can("analytics")) return;
    try { setAnalytics(await fetchAnalytics()); }
    catch { toast.error("Failed to load analytics"); }
  };

  useEffect(() => { if (user) reload(); }, [user]);
  useEffect(() => {
    if (user && tab === "analytics") loadAnalytics();
  }, [user, tab]);

  // Redirect to allowed tab if editor lands on restricted one
  useEffect(() => {
    if (user && !isSuperAdmin && ["users", "settings", "audit"].includes(tab)) {
      setTab("items");
    }
  }, [user, tab, isSuperAdmin]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginBusy(true);
    try {
      await adminLogin(email, password);
      await refreshMe();
      toast.success(t.signIn);
    } catch (err) {
      if (err?.response?.status === 429) {
        const retryAfter = parseInt(err.response.headers?.["retry-after"], 10) || 60;
        setRetryAt(Math.floor(Date.now() / 1000) + retryAfter);
        toast.error(t.tooManyAttempts);
      } else {
        toast.error(t.loginFailed);
      }
    } finally { setLoginBusy(false); }
  };

  const retrySecondsLeft = Math.max(0, retryAt - now);

  const openNew = () => {
    const defaultCat = categories[0]?.key || "";
    setEditing(null);
    setForm({ ...emptyForm, category: defaultCat });
    setDialogOpen(true);
  };
  const toggleActive = async (it, v) => {
    setItems((prev) => prev.map((x) => (x.id === it.id ? { ...x, is_active: v } : x)));
    try {
      await updateItem(it.id, { is_active: v });
      toast.success(v ? t.itemShown : t.itemHidden);
    } catch {
      setItems((prev) => prev.map((x) => (x.id === it.id ? { ...x, is_active: !v } : x)));
      toast.error("Failed");
    }
  };
  const openEdit = (it) => {
    setEditing(it);
    setForm({
      name_en: it.name_en, name_ar: it.name_ar,
      description_en: it.description_en, description_ar: it.description_ar,
      category: it.category, price: it.price, calories: it.calories,
      protein_g: it.protein_g ?? 0, carbs_g: it.carbs_g ?? 0, fat_g: it.fat_g ?? 0,
      image_url: it.image_url, is_featured: !!it.is_featured, kitchen_required: !!it.kitchen_required,
      sizes: it.sizes || [], milk_options: it.milk_options || [],
    });
    setDialogOpen(true);
  };
  const openDuplicate = (it) => {
    // Clone into a NEW item (editing = null) so Save creates a copy.
    setEditing(null);
    setForm({
      name_en: it.name_en ? `${it.name_en} (Copy)` : "",
      name_ar: it.name_ar ? `${it.name_ar} (نسخة)` : "",
      description_en: it.description_en, description_ar: it.description_ar,
      category: it.category, price: it.price, calories: it.calories,
      protein_g: it.protein_g ?? 0, carbs_g: it.carbs_g ?? 0, fat_g: it.fat_g ?? 0,
      image_url: it.image_url, is_featured: false, kitchen_required: !!it.kitchen_required,
      sizes: (it.sizes || []).map((v) => ({ ...v })),
      milk_options: (it.milk_options || []).map((v) => ({ ...v })),
  is_active: true,
    });
    setDialogOpen(true);
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadImage(file);
      setForm((f) => ({ ...f, image_url: res.url }));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(getErrorMessage(err, "Upload failed"));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const cleanVariants = (arr) =>
      (arr || [])
        .filter((v) => (v.name_en && v.name_en.trim()) || (v.name_ar && v.name_ar.trim()))
        .map((v) => ({
          id: v.id,
          name_en: v.name_en || "", name_ar: v.name_ar || "",
          price: parseFloat(v.price || 0) || 0,
          calories: parseFloat(v.calories || 0) || 0,
          protein_g: parseFloat(v.protein_g || 0) || 0,
          carbs_g: parseFloat(v.carbs_g || 0) || 0,
          fat_g: parseFloat(v.fat_g || 0) || 0,
        }));
    const payload = {
      ...form,
      price: parseFloat(form.price),
      calories: parseFloat(form.calories),
      protein_g: parseFloat(form.protein_g || 0),
      carbs_g: parseFloat(form.carbs_g || 0),
      fat_g: parseFloat(form.fat_g || 0),
      sizes: cleanVariants(form.sizes),
      milk_options: cleanVariants(form.milk_options),
    };
    try {
      if (editing) { await updateItem(editing.id, payload); toast.success(t.itemUpdated); }
      else { await createItem(payload); toast.success(t.itemAdded); }
      setDialogOpen(false);
      reload();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const handleDelete = async (it) => {
    if (!window.confirm(t.confirmDelete)) return;
    try { await deleteItem(it.id); toast.success(t.itemDeleted); reload(); }
    catch (err) { toast.error(getErrorMessage(err)); }
  };

  if (checking) return <div className="min-h-screen bg-[#FDFBF7]"><BrandHeader /></div>;

  if (!user) {
    return (
      <div className="min-h-screen bg-[#FDFBF7]">
        <BrandHeader />
        <div className="max-w-md mx-auto px-6 py-24">
          {authView === "forgot" && (
            <ForgotPasswordForm onBack={() => setAuthView("login")} />
          )}
          {authView === "reset" && resetTokenFromUrl && (
            <ResetPasswordForm
              token={resetTokenFromUrl}
              onDone={() => { navigate("/admin", { replace: true }); setAuthView("login"); }}
            />
          )}
          {authView === "login" && (
            <>
              <h1 className={`${lang === "ar" ? "font-serif-ar" : "font-serif-en"} text-4xl text-[#0F5F63] font-semibold`}>
                {t.adminTitle}
              </h1>
              <form onSubmit={handleLogin} className="mt-8 space-y-4" data-testid="admin-login-form">
                <div>
                  <Label htmlFor="email" className="text-xs uppercase tracking-widest text-[#5C6B6D]">{t.email}</Label>
                  <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                    data-testid="admin-email-input"
                    className="mt-2 rounded-full bg-[#F4F2EB] border-0 focus-visible:ring-1 focus-visible:ring-[#0F5F63] h-12 px-5"
                    placeholder="admin@zed.com" autoFocus />
                </div>
                <div>
                  <Label htmlFor="pw" className="text-xs uppercase tracking-widest text-[#5C6B6D]">{t.password}</Label>
                  <Input id="pw" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                    data-testid="admin-password-input"
                    className="mt-2 rounded-full bg-[#F4F2EB] border-0 focus-visible:ring-1 focus-visible:ring-[#0F5F63] h-12 px-5"
                    placeholder="••••••••" />
                </div>
                <Button type="submit" disabled={loginBusy || retrySecondsLeft > 0} data-testid="admin-login-btn"
                  className="w-full rounded-full h-12 bg-[#0F5F63] hover:bg-[#0A4548] text-[#FDFBF7] uppercase tracking-widest text-xs disabled:opacity-50">
                  {retrySecondsLeft > 0
                    ? `${t.retryIn} ${retrySecondsLeft}${t.seconds}`
                    : t.signIn}
                </Button>
                {retrySecondsLeft > 0 && (
                  <div data-testid="retry-countdown" className="rounded-2xl bg-[#E27B58]/10 text-[#E27B58] px-4 py-3 text-xs text-center">
                    {t.tooManyAttempts} — {t.retryIn} <span className="font-semibold" data-testid="retry-countdown-seconds">{retrySecondsLeft}</span>{t.seconds}
                  </div>
                )}
                <div className="text-center pt-2">
                  <button type="button" onClick={() => setAuthView("forgot")}
                    data-testid="forgot-password-link"
                    className="text-xs uppercase tracking-widest text-[#5C6B6D] hover:text-[#0F5F63]">
                    {t.forgotPassword}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <BrandHeader />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div>
            <h1 className={`${lang === "ar" ? "font-serif-ar" : "font-serif-en"} text-3xl lg:text-4xl text-[#0F5F63] font-semibold`}>
              {t.adminTitle}
            </h1>
            <p className="text-sm text-[#5C6B6D] mt-1">
              {user.name} · <span className="text-[#0F5F63]">{user.role === "super_admin" ? t.superAdmin : t.editor}</span>
            </p>
          </div>
          <div className="flex gap-3">
            {tab === "items" && can("menu") && (
              <Button onClick={openNew} data-testid="add-item-btn"
                className="rounded-full bg-[#0F5F63] hover:bg-[#0A4548] text-[#FDFBF7] uppercase tracking-widest text-xs h-11 px-5">
                <Plus className="w-4 h-4 me-2" /> {t.addItem}
              </Button>
            )}
            <Button onClick={logout} data-testid="admin-logout-btn" variant="outline"
              className="rounded-full border-[#0F5F63]/20 text-[#0F5F63] hover:bg-[#F4F2EB] uppercase tracking-widest text-xs h-11 px-5">
              {t.logout}
            </Button>
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="mb-6 bg-[#F4F2EB] rounded-full p-1 flex-wrap h-auto">
            <TabsTrigger value="items" data-testid="tab-items" className="rounded-full px-5 uppercase tracking-widest text-xs data-[state=active]:bg-[#0F5F63] data-[state=active]:text-[#FDFBF7]">{t.tabItems}</TabsTrigger>
            <TabsTrigger value="categories" data-testid="tab-categories" className="rounded-full px-5 uppercase tracking-widest text-xs data-[state=active]:bg-[#0F5F63] data-[state=active]:text-[#FDFBF7]">{t.tabCategories}</TabsTrigger>
            {can("promotions") && (
              <TabsTrigger value="promotions" data-testid="tab-promotions" className="rounded-full px-5 uppercase tracking-widest text-xs data-[state=active]:bg-[#0F5F63] data-[state=active]:text-[#FDFBF7]">{t.tabPromotions}</TabsTrigger>
            )}
            {can("analytics") && (
              <TabsTrigger value="analytics" data-testid="tab-analytics" className="rounded-full px-5 uppercase tracking-widest text-xs data-[state=active]:bg-[#0F5F63] data-[state=active]:text-[#FDFBF7]">{t.tabAnalytics}</TabsTrigger>
            )}
            {isSuperAdmin && (
              <TabsTrigger value="users" data-testid="tab-users" className="rounded-full px-5 uppercase tracking-widest text-xs data-[state=active]:bg-[#0F5F63] data-[state=active]:text-[#FDFBF7]">{t.tabUsers}</TabsTrigger>
            )}
            {isSuperAdmin && (
              <TabsTrigger value="settings" data-testid="tab-settings" className="rounded-full px-5 uppercase tracking-widest text-xs data-[state=active]:bg-[#0F5F63] data-[state=active]:text-[#FDFBF7]">{t.tabSettings}</TabsTrigger>
            )}
            {isSuperAdmin && (
              <TabsTrigger value="audit" data-testid="tab-audit" className="rounded-full px-5 uppercase tracking-widest text-xs data-[state=active]:bg-[#0F5F63] data-[state=active]:text-[#FDFBF7]">{t.tabAudit}</TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="items">
            <div className="rounded-2xl bg-white border border-black/5 overflow-hidden">
              <div className="grid grid-cols-12 gap-4 px-6 py-4 bg-[#F4F2EB] text-xs uppercase tracking-widest text-[#5C6B6D]">
                <div className="col-span-1">{t.image}</div>
                <div className="col-span-2">{t.name}</div>
                <div className="col-span-1">{t.active}</div>
                <div className="col-span-2">{t.category}</div>
                <div className="col-span-1">{t.price}</div>
                <div className="col-span-1">{t.calories}</div>
                <div className="col-span-1">{t.views}</div>
                <div className="col-span-1">{t.featured}</div>
                <div className="col-span-2 text-end">{t.actions}</div>
              </div>
              {loading ? (
                <div className="p-6 text-sm text-[#5C6B6D]">…</div>
              ) : (
                items.map((it) => (
                  <div key={it.id} className="grid grid-cols-12 gap-4 px-6 py-4 border-t border-black/5 items-center text-sm" data-testid={`admin-row-${it.id}`}>
                    <div className="col-span-1"><img src={it.image_url} alt="" className="w-12 h-12 rounded-lg object-cover" /></div>
                    <div className={`col-span-2 ${it.is_active === false ? "opacity-50" : ""}`}>
                      <p className="font-medium text-[#1A1A1A]">{it.name_en}</p>
                      <p className="text-xs text-[#5C6B6D] font-serif-ar mt-0.5">{it.name_ar}</p>
                    </div>
                    <div className="col-span-1">
                      <Switch checked={it.is_active !== false} onCheckedChange={(v) => toggleActive(it, v)} data-testid={`active-switch-${it.id}`} />
                    </div>
                    <div className="col-span-2 text-[#5C6B6D]">{nameFor(it.category, lang)}</div>
                    <div className="col-span-1">{it.price} {t.currency}</div>
                    <div className="col-span-1">{it.calories} {t.kcal}</div>
                    <div className="col-span-1 inline-flex items-center gap-1 text-[#5C6B6D]"><Eye className="w-3.5 h-3.5" />{it.views || 0}</div>
                    <div className="col-span-1">{it.is_featured ? <span className="text-[#E27B58] text-xs">★</span> : <span className="text-[#5C6B6D]/40 text-xs">—</span>}</div>
                    <div className="col-span-2 flex justify-end gap-2">
                      <Button variant="ghost" size="icon" onClick={() => openEdit(it)} data-testid={`edit-btn-${it.id}`}
                        className="rounded-full text-[#0F5F63] hover:bg-[#F4F2EB]"><Pencil className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="icon" onClick={() => openDuplicate(it)} data-testid={`duplicate-btn-${it.id}`}
                        title={t.duplicate} className="rounded-full text-[#5C6B6D] hover:bg-[#F4F2EB]"><Copy className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(it)} data-testid={`delete-btn-${it.id}`}
                        className="rounded-full text-[#E27B58] hover:bg-[#E27B58]/10"><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </TabsContent>

          <TabsContent value="categories">
            <CategoryManager items={items} />
          </TabsContent>

          {can("promotions") && (
            <TabsContent value="promotions"><PromotionsManager /></TabsContent>
          )}

          {can("analytics") && (
            <TabsContent value="analytics">
              <div data-testid="analytics-panel">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                  <div className="rounded-2xl bg-[#0F5F63] text-[#FDFBF7] p-6">
                    <p className="text-[10px] uppercase tracking-[0.3em] opacity-80">{t.totalViews}</p>
                    <p className="font-serif-en text-5xl mt-3" data-testid="total-views">{analytics?.total_views ?? "…"}</p>
                  </div>
                  <div className="rounded-2xl bg-[#F4F2EB] p-6">
                    <p className="text-[10px] uppercase tracking-[0.3em] text-[#5C6B6D]">{t.tabItems}</p>
                    <p className="font-serif-en text-5xl mt-3 text-[#0F5F63]">{items.length}</p>
                  </div>
                  <div className="rounded-2xl bg-[#F4F2EB] p-6">
                    <p className="text-[10px] uppercase tracking-[0.3em] text-[#5C6B6D]">{t.featured}</p>
                    <p className="font-serif-en text-5xl mt-3 text-[#0F5F63]">{items.filter((i) => i.is_featured).length}</p>
                  </div>
                </div>
                <div className="rounded-2xl bg-white border border-black/5 overflow-hidden">
                  <div className="px-6 py-4 bg-[#F4F2EB] text-xs uppercase tracking-widest text-[#5C6B6D]">{t.topItems}</div>
                  {!analytics ? (
                    <div className="p-6 text-sm text-[#5C6B6D]">…</div>
                  ) : analytics.items.filter((i) => i.views > 0).length === 0 ? (
                    <div className="p-8 text-sm text-[#5C6B6D] text-center">{t.noViewsYet}</div>
                  ) : (
                    analytics.items.slice(0, 10).map((it, idx) => {
                      const max = Math.max(1, ...analytics.items.map((x) => x.views));
                      const pct = Math.max(4, Math.round((it.views / max) * 100));
                      return (
                        <div key={it.id} className="px-6 py-3 border-t border-black/5 flex items-center gap-4 text-sm" data-testid={`analytics-row-${it.id}`}>
                          <span className="text-xs text-[#5C6B6D] w-6">{idx + 1}</span>
                          <span className="flex-1 font-medium">{lang === "ar" ? it.name_ar : it.name_en}</span>
                          <div className="flex-1 max-w-xs h-1.5 rounded-full bg-[#F4F2EB] overflow-hidden">
                            <div className="h-full bg-[#0F5F63] rounded-full" style={{ width: `${pct}%` }} />
                          </div>
                          <span className="w-16 text-end text-[#5C6B6D]">{it.views}</span>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </TabsContent>
          )}

          {isSuperAdmin && (
            <TabsContent value="users"><UsersManager /></TabsContent>
          )}
          {isSuperAdmin && (
            <TabsContent value="settings"><SettingsManager /></TabsContent>
          )}
          {isSuperAdmin && (
            <TabsContent value="audit"><AuditLog /></TabsContent>
          )}
        </Tabs>
      </div>

      {/* Item form dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="w-[95vw] max-w-4xl bg-[#FDFBF7] rounded-2xl max-h-[92vh] overflow-y-auto" data-testid="item-form-dialog">
          <DialogHeader>
            <DialogTitle className={`${lang === "ar" ? "font-serif-ar" : "font-serif-en"} text-2xl text-[#0F5F63]`}>
              {editing ? t.editItem : t.addItem}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name_en">{t.nameEn}</Label>
                <Input id="name_en" required value={form.name_en} onChange={(e) => setForm({ ...form, name_en: e.target.value })} data-testid="form-name-en" />
              </div>
              <div>
                <Label htmlFor="name_ar">{t.nameAr}</Label>
                <Input id="name_ar" required value={form.name_ar} onChange={(e) => setForm({ ...form, name_ar: e.target.value })} data-testid="form-name-ar" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="descEn">{t.descEn}</Label>
                <Textarea id="descEn" value={form.description_en} onChange={(e) => setForm({ ...form, description_en: e.target.value })} rows={2} />
              </div>
              <div>
                <Label htmlFor="descAr">{t.descAr}</Label>
                <Textarea id="descAr" value={form.description_ar} onChange={(e) => setForm({ ...form, description_ar: e.target.value })} rows={2} />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>{t.category}</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="form-category-trigger"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    {categories.map((c) => (
                      <SelectItem key={c.id} value={c.key} data-testid={`form-category-opt-${c.key}`}>
                        {lang === "ar" ? c.name_ar : c.name_en}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="price">{t.price} ({t.currency})</Label>
                <Input id="price" required type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="form-price" />
              </div>
              <div>
                <Label htmlFor="cal">{t.calories} ({t.kcal})</Label>
                <Input id="cal" required type="number" step="any" min="0" value={form.calories} onChange={(e) => setForm({ ...form, calories: e.target.value })} data-testid="form-calories" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="protein">{t.protein} ({t.grams})</Label>
                <Input id="protein" type="number" step="any" min="0" value={form.protein_g} onChange={(e) => setForm({ ...form, protein_g: e.target.value })} data-testid="form-protein" />
              </div>
              <div>
                <Label htmlFor="carbs">{t.carbs} ({t.grams})</Label>
                <Input id="carbs" type="number" step="any" min="0" value={form.carbs_g} onChange={(e) => setForm({ ...form, carbs_g: e.target.value })} data-testid="form-carbs" />
              </div>
              <div>
                <Label htmlFor="fat">{t.fat} ({t.grams})</Label>
                <Input id="fat" type="number" step="any" min="0" value={form.fat_g} onChange={(e) => setForm({ ...form, fat_g: e.target.value })} data-testid="form-fat" />
              </div>
            </div>
            <div>
              <Label htmlFor="img">{t.imageUrl}</Label>
              <div className="flex gap-2 mt-1 items-center">
                <Input id="img" required value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} data-testid="form-image-url" placeholder="https://…" />
                <input ref={fileRef} type="file" accept="image/*" hidden onChange={handleUpload} data-testid="form-image-file" />
                <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading}
                  data-testid="form-upload-btn"
                  className="rounded-full border-[#0F5F63]/20 text-[#0F5F63] whitespace-nowrap">
                  <Upload className="w-4 h-4 me-2" />
                  {uploading ? t.uploading : t.uploadImage}
                </Button>
              </div>
              {form.image_url && (<img src={form.image_url} alt="preview" className="mt-3 w-24 h-24 rounded-lg object-cover" />)}
            </div>
            <div className="flex items-start gap-3 rounded-xl bg-[#F4F2EB] px-4 py-3">
              <Switch id="item_active" checked={form.is_active !== false} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="form-active-switch" />
              <div>
                <Label htmlFor="item_active" className="cursor-pointer">{t.active}</Label>
                <p className="text-[10px] text-[#5C6B6D] mt-1">{t.activeHint}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Switch id="featured" checked={form.is_featured} onCheckedChange={(v) => setForm({ ...form, is_featured: v })} data-testid="form-featured-switch" />
              <Label htmlFor="featured" className="cursor-pointer">{t.featured}</Label>
            </div>
            <div className="flex items-start gap-3 rounded-xl bg-[#F4F2EB] px-4 py-3">
              <Switch
                id="item_kitchen"
                checked={!!form.kitchen_required}
                onCheckedChange={(v) => setForm({ ...form, kitchen_required: v })}
                data-testid="form-kitchen-switch"
              />
              <div>
                <Label htmlFor="item_kitchen" className="cursor-pointer">{t.kitchenRequired}</Label>
                <p className="text-[10px] text-[#5C6B6D] mt-1">{t.kitchenRequiredHint}</p>
              </div>
            </div>
            <VariantsEditor
              title={t.sizesLabel}
              testid="sizes-editor"
              rows={form.sizes}
              onChange={(rows) => setForm({ ...form, sizes: rows })}
            />
            <VariantsEditor
              title={t.milkOptionsLabel}
              testid="milk-editor"
              rows={form.milk_options}
              onChange={(rows) => setForm({ ...form, milk_options: rows })}
            />
            <DialogFooter className="pt-4">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>{t.cancel}</Button>
              <Button type="submit" className="bg-[#0F5F63] hover:bg-[#0A4548] text-[#FDFBF7]" data-testid="form-save-btn">{t.save}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
